// Lightweight, dependency-free markdown → HTML for the News Watch article
// editor. Handles the subset Nemotron emits: ## / ### headings, **bold**,
// *italic*, [links](url), - / 1. lists, > blockquote, `code`, and paragraphs.
// Two flavors: `renderPreview` (MUI-styled inline, for the in-app preview) and
// `toPublishableHtml` (clean semantic HTML + JSON-LD, for Copy-as-HTML export).

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function inline(s: string): string {
  return esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
}

interface Block {
  type: 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'quote' | 'table';
  text?: string;
  items?: string[];
  header?: string[];
  rows?: string[][];
}

/** A GFM separator cell: optional colons + ≥2 dashes (`---`, `:--`, `:-:`). */
const SEP_CELL = /^:?-{2,}:?$/;

/**
 * Parse a (possibly GLUED single-line) pipe table starting at `lines[i]`. Nemotron
 * sometimes emits the whole table on ONE physical line — header + `|---|` separator
 * + every row run together with `| |` row boundaries and no newlines — so a purely
 * line-based extractor misses it. We instead: gather the table text (this line plus
 * any following pipe lines), find the separator group to learn the column count N,
 * flatten every cell, drop the outer/boundary empties + separator cells, and
 * re-chunk into rows of N. Returns the block + the next line index, or null.
 */
function takeTable(lines: string[], i: number): { block: Block; next: number } | null {
  const first = lines[i].trim();
  if (!first.includes('|')) return null;

  // The table region = this line + subsequent lines that still look like pipe rows.
  let j = i;
  const buf: string[] = [];
  while (j < lines.length && lines[j].trim().includes('|')) { buf.push(lines[j].trim()); j++; }
  const joined = buf.join(' ');

  // Flatten to cells; a separator group must be present (that's what makes it a table).
  const cells = joined.split('|').map((c) => c.trim());
  const sepIdx = cells.findIndex((c) => SEP_CELL.test(c.replace(/\s/g, '')));
  if (sepIdx < 0) return null;

  // Column count = contiguous separator cells; header = the N non-empty cells before them.
  let n = 0;
  for (let k = sepIdx; k < cells.length && SEP_CELL.test(cells[k].replace(/\s/g, '')); k++) n++;
  if (n < 1) return null;
  const header = cells.slice(0, sepIdx).filter((c) => c !== '').slice(-n);
  if (header.length !== n) return null;

  // Body = every remaining non-empty, non-separator cell, chunked into rows of N.
  const bodyCells = cells.slice(sepIdx + n).filter((c) => c !== '' && !SEP_CELL.test(c.replace(/\s/g, '')));
  const rows: string[][] = [];
  for (let k = 0; k < bodyCells.length; k += n) {
    const row = bodyCells.slice(k, k + n);
    while (row.length < n) row.push('');
    rows.push(row);
  }
  return { block: { type: 'table', header, rows }, next: j };
}

function parse(md: string): Block[] {
  const lines = (md || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (!t) { i++; continue; }

    if (/^###\s+/.test(t)) { blocks.push({ type: 'h3', text: t.replace(/^###\s+/, '') }); i++; continue; }
    if (/^##\s+/.test(t)) { blocks.push({ type: 'h2', text: t.replace(/^#{1,2}\s+/, '') }); i++; continue; }
    if (/^#\s+/.test(t)) { blocks.push({ type: 'h2', text: t.replace(/^#\s+/, '') }); i++; continue; }

    // Pipe table (multi-line OR glued on one line).
    if (t.includes('|')) {
      const tbl = takeTable(lines, i);
      if (tbl) { blocks.push(tbl.block); i = tbl.next; continue; }
    }

    if (/^>\s?/.test(t)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) { buf.push(lines[i].trim().replace(/^>\s?/, '')); i++; }
      blocks.push({ type: 'quote', text: buf.join(' ') });
      continue;
    }
    if (/^[-*]\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^[-*]\s+/, '')); i++; }
      blocks.push({ type: 'ul', items });
      continue;
    }
    if (/^\d+\.\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+\.\s+/, '')); i++; }
      blocks.push({ type: 'ol', items });
      continue;
    }
    // paragraph — accumulate until blank line (stop at a pipe line so a table
    // isn't swallowed into a paragraph).
    const buf: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].includes('|') &&
      !/^(#{1,3}\s|[-*]\s|\d+\.\s|>\s?)/.test(lines[i].trim())
    ) {
      buf.push(lines[i].trim()); i++;
    }
    // A lone pipe line that isn't a real table (no separator) → treat as text.
    if (!buf.length) { blocks.push({ type: 'p', text: t }); i++; continue; }
    blocks.push({ type: 'p', text: buf.join(' ') });
  }
  return blocks;
}

/** MUI-styled HTML for the in-app preview pane. */
export function renderPreview(md: string): string {
  return parse(md).map((b) => {
    switch (b.type) {
      case 'h2': return `<h2 style="font-size:22px;font-weight:750;margin:28px 0 12px;letter-spacing:-.3px">${inline(b.text || '')}</h2>`;
      case 'h3': return `<h3 style="font-size:17px;font-weight:700;margin:22px 0 10px">${inline(b.text || '')}</h3>`;
      case 'quote': return `<blockquote style="border-left:3px solid #7C3AED;background:#F5F1FF;margin:18px 0;padding:14px 18px;border-radius:0 8px 8px 0;color:#3F2A66;font-style:italic">${inline(b.text || '')}</blockquote>`;
      case 'ul': return `<ul style="margin:0 0 16px;padding-left:22px">${(b.items || []).map((it) => `<li style="margin-bottom:6px">${inline(it)}</li>`).join('')}</ul>`;
      case 'ol': return `<ol style="margin:0 0 16px;padding-left:22px">${(b.items || []).map((it) => `<li style="margin-bottom:6px">${inline(it)}</li>`).join('')}</ol>`;
      case 'table': {
        const thead = `<thead><tr>${(b.header || []).map((c) => `<th style="text-align:left;padding:8px 12px;border:1px solid #E3DCF5;background:#F5F1FF;font-weight:700;font-size:14px">${inline(c)}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${(b.rows || []).map((r) => `<tr>${r.map((c) => `<td style="padding:8px 12px;border:1px solid #EEE9F7;vertical-align:top;font-size:14px">${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
        return `<div style="overflow-x:auto;margin:18px 0"><table style="border-collapse:collapse;width:100%">${thead}${tbody}</table></div>`;
      }
      default: return `<p style="margin:0 0 16px;line-height:1.75">${inline(b.text || '')}</p>`;
    }
  }).join('\n');
}

/** Clean semantic HTML (+ optional JSON-LD Article) for Copy-as-HTML export. */
export function toPublishableHtml(md: string, meta?: { title?: string; description?: string }): string {
  const body = parse(md).map((b) => {
    switch (b.type) {
      case 'h2': return `  <h2>${inline(b.text || '')}</h2>`;
      case 'h3': return `  <h3>${inline(b.text || '')}</h3>`;
      case 'quote': return `  <blockquote>${inline(b.text || '')}</blockquote>`;
      case 'ul': return `  <ul>\n${(b.items || []).map((it) => `    <li>${inline(it)}</li>`).join('\n')}\n  </ul>`;
      case 'ol': return `  <ol>\n${(b.items || []).map((it) => `    <li>${inline(it)}</li>`).join('\n')}\n  </ol>`;
      case 'table': {
        const thead = `    <thead><tr>${(b.header || []).map((c) => `<th>${inline(c)}</th>`).join('')}</tr></thead>`;
        const tbody = `    <tbody>${(b.rows || []).map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
        return `  <table>\n${thead}\n${tbody}\n  </table>`;
      }
      default: return `  <p>${inline(b.text || '')}</p>`;
    }
  }).join('\n');

  const jsonLd = meta?.title
    ? `\n<script type="application/ld+json">\n${JSON.stringify(
        { '@context': 'https://schema.org', '@type': 'Article', headline: meta.title, description: meta.description || '' },
        null, 2,
      )}\n</script>\n`
    : '';

  return `<article>\n${meta?.title ? `  <h1>${esc(meta.title)}</h1>\n` : ''}${body}\n</article>${jsonLd}`;
}

/** Plain-text word count (mirrors the agent's count for display parity). */
export function wordCount(md: string): number {
  return (md || '').match(/\b\w+\b/gu)?.length ?? 0;
}
